import { Empty as $Empty, CustomType as $CustomType } from "../gleam.mjs";

/**
 * Represents the input data needed for an entity to update.
 */
export class BehaviorInput extends $CustomType {
  constructor(entity, additional_inputs) {
    super();
    this.entity = entity;
    this.additional_inputs = additional_inputs;
  }
}
export const BehaviorInput$BehaviorInput = (entity, additional_inputs) =>
  new BehaviorInput(entity, additional_inputs);
export const BehaviorInput$isBehaviorInput = (value) =>
  value instanceof BehaviorInput;
export const BehaviorInput$BehaviorInput$entity = (value) => value.entity;
export const BehaviorInput$BehaviorInput$0 = (value) => value.entity;
export const BehaviorInput$BehaviorInput$additional_inputs = (value) =>
  value.additional_inputs;
export const BehaviorInput$BehaviorInput$1 = (value) => value.additional_inputs;

/**
 * Represents the result of an entity update.
 */
export class BehaviorResult extends $CustomType {
  constructor(success, entity, additional_outputs) {
    super();
    this.success = success;
    this.entity = entity;
    this.additional_outputs = additional_outputs;
  }
}
export const BehaviorResult$BehaviorResult = (success, entity, additional_outputs) =>
  new BehaviorResult(success, entity, additional_outputs);
export const BehaviorResult$isBehaviorResult = (value) =>
  value instanceof BehaviorResult;
export const BehaviorResult$BehaviorResult$success = (value) => value.success;
export const BehaviorResult$BehaviorResult$0 = (value) => value.success;
export const BehaviorResult$BehaviorResult$entity = (value) => value.entity;
export const BehaviorResult$BehaviorResult$1 = (value) => value.entity;
export const BehaviorResult$BehaviorResult$additional_outputs = (value) =>
  value.additional_outputs;
export const BehaviorResult$BehaviorResult$2 = (value) =>
  value.additional_outputs;

/**
 * Creates a behavior tree that performs the given behavior tree but always succeeds.
 */
export function true$(bt) {
  return (input) => {
    let $ = bt(input);
    let e;
    let a;
    e = $.entity;
    a = $.additional_outputs;
    return new BehaviorResult(true, e, a);
  };
}

/**
 * Creates a behavior tree that performs the given behavior tree but returns the opposite for success.
 */
export function not(bt) {
  return (input) => {
    let $ = bt(input);
    let s;
    let e;
    let a;
    s = $.success;
    e = $.entity;
    a = $.additional_outputs;
    return new BehaviorResult(!s, e, a);
  };
}

/**
 * Creates a behavior tree that performs all the given behavior trees until one succeeds.
 * If none succeed, the last behavior tree's outputs are returned and the whole tree fails.
 */
export function selector(btrees, default_output, output_to_input, output_merge) {
  if (btrees instanceof $Empty) {
    return (input) => {
      return new BehaviorResult(false, input.entity, default_output);
    };
  } else {
    let bt = btrees.head;
    let rest = btrees.tail;
    return (input) => {
      let $ = bt(input);
      let success;
      let e;
      let first_behavior_outputs;
      success = $.success;
      e = $.entity;
      first_behavior_outputs = $.additional_outputs;
      if (success) {
        return new BehaviorResult(true, e, first_behavior_outputs);
      } else {
        let next_input = new BehaviorInput(
          e,
          output_to_input(input.additional_inputs, first_behavior_outputs),
        );
        let $1 = selector(rest, default_output, output_to_input, output_merge)(
          next_input,
        );
        let success$1;
        let e$1;
        let rest_outputs;
        success$1 = $1.success;
        e$1 = $1.entity;
        rest_outputs = $1.additional_outputs;
        let final_outputs = output_merge(first_behavior_outputs, rest_outputs);
        return new BehaviorResult(success$1, e$1, final_outputs);
      }
    };
  }
}

/**
 * Creates a behavior tree that performs all the given behavior trees until one fails.
 * If all succeed, the last behavior tree's outputs are returned and the whole tree succeeds.
 */
export function sequence(btrees, default_output, output_to_input, output_merge) {
  if (btrees instanceof $Empty) {
    return (input) => {
      return new BehaviorResult(true, input.entity, default_output);
    };
  } else {
    let bt = btrees.head;
    let rest = btrees.tail;
    return (input) => {
      let $ = bt(input);
      let success;
      let e;
      let first_behavior_outputs;
      success = $.success;
      e = $.entity;
      first_behavior_outputs = $.additional_outputs;
      if (success) {
        let next_input = new BehaviorInput(
          e,
          output_to_input(input.additional_inputs, first_behavior_outputs),
        );
        let $1 = sequence(rest, default_output, output_to_input, output_merge)(
          next_input,
        );
        let success$1;
        let e$1;
        let rest_outputs;
        success$1 = $1.success;
        e$1 = $1.entity;
        rest_outputs = $1.additional_outputs;
        let final_outputs = output_merge(first_behavior_outputs, rest_outputs);
        return new BehaviorResult(success$1, e$1, final_outputs);
      } else {
        return new BehaviorResult(
          false,
          e,
          output_merge(default_output, first_behavior_outputs),
        );
      }
    };
  }
}

/**
 * Creates a behavior tree that performs all the given behavior trees and always succeeds.
 */
export function all(btrees, default_output, output_to_input, output_merge) {
  if (btrees instanceof $Empty) {
    return (input) => {
      return new BehaviorResult(true, input.entity, default_output);
    };
  } else {
    let bt = btrees.head;
    let rest = btrees.tail;
    return (input) => {
      let $ = bt(input);
      let e;
      let first_behavior_outputs;
      e = $.entity;
      first_behavior_outputs = $.additional_outputs;
      let next_input = new BehaviorInput(
        e,
        output_to_input(input.additional_inputs, first_behavior_outputs),
      );
      let $1 = all(rest, default_output, output_to_input, output_merge)(
        next_input,
      );
      let success;
      let e$1;
      let rest_outputs;
      success = $1.success;
      e$1 = $1.entity;
      rest_outputs = $1.additional_outputs;
      let final_outputs = output_merge(first_behavior_outputs, rest_outputs);
      return new BehaviorResult(success, e$1, final_outputs);
    };
  }
}
